import { Component, h, Host, Listen, Method, Prop, State, EventEmitter, Event, Watch, Element } from '@stencil/core'
import { DropdownItemSelected, DropdownMenuItem, DropdownMenuItemClicked } from '../../utils/interfaces'
import { ICON_TYPES } from '../../utils'
@Component({
  tag: 'snap-dropdown',
  styleUrl: 'snap-dropdown.scss',
  shadow: true,
})
export class SnapDropdown {
  @Prop() options: DropdownMenuItem[] = []
  @Prop() title: string
  @Prop() description: string
  @Prop() selected: string
  @Prop() value: any
  @Prop() buttonText: string
  @Prop() hideButton: boolean = false
  @Prop() minimal = false
  @Prop() leftHang: boolean = false

  /**
   * By default we will keep track of and highlight the current value of the dropdown
   */
  @Prop() trackCurrentSelection: boolean = true

  @Prop({ reflect: true }) size: 'xs' | 'sm' | 'base' | 'lg' | 'xl' = 'base'
  @Prop({ reflect: true }) icon: ICON_TYPES | undefined = undefined
  @Prop({ reflect: true }) indicator: string | undefined = undefined
  @Prop({ reflect: true }) disabled: boolean = false
  @Prop({ reflect: true }) leadingLabel: string = ''
  @Prop({ reflect: true }) avatarImg: string = ''
  @Prop({ reflect: true }) switchProfileLink: string = ''
  @Prop({ reflect: true }) modalType: 'fullscreen' | 'drawer' = 'fullscreen'
  @Prop({ reflect: true }) searchField: boolean = false
  @Prop({ reflect: true }) multi: boolean = false
  @Prop({ reflect: true }) yearSelector: boolean = false
  @Prop({ reflect: true }) setYear: string = ''
  @Prop({ reflect: true }) maxHeight: string = ''
  @Prop({ reflect: true }) searchMethod: 'start' | 'contain' = 'start'
  @Prop( { reflect: true }) menuPosition: 'top' | 'bottom' = 'bottom'

  @State() isOpen: boolean = false
  @State() isMobile: boolean
  @State() optionsCopy: DropdownMenuItem[] = []
  @State() filteredOptionsCopy: DropdownMenuItem[] = []
  @State() lastChanged = new Date().getTime()
  @State() searchWord: ''
  @State() selectedItem = ''
  @State() selectedItemBadgeText = ''
  @State() itemSelected = 0
  @State() currentYear: number = null
  @State() selectedYear: number = null

  @Element() private element: HTMLElement
  private textInput!: HTMLInputElement
  private mobileScreenWidth = 768

  @Event({ eventName: 'snap-dropdown-item-selected' }) dropdownItemSelected: EventEmitter<DropdownItemSelected>
  @Event({ eventName: 'snap-dropdown-updated' }) dropdownUpdated: EventEmitter<DropdownItemSelected[]>
  @Event({ eventName: 'snap-dropdown-closed' }) dropdownClosed: EventEmitter<boolean>

  @Listen('click', { target: 'window' })
  checkForClickOutside(e: any) {
    const now = new Date().getTime()

    if (!!this.isOpen && !this.element.contains(e.target) && now - this.lastChanged > 500) {
      this.toggleDropdown()
      this.dropdownClosed.emit(true)
    }
  }

  @Listen('resize', { target: 'window' })
  checkScreen() {
    this.isMobile = document.body.clientWidth < this.mobileScreenWidth
  }

  @Watch('options')
  onOptionsUpdate() {
    this.optionsCopy = [...this.options]
    this.filteredOptionsCopy = [...this.options]

    const selectedItemIndex = this.optionsCopy.findIndex(item => item.selected)

    if (selectedItemIndex !== -1) {
      this.selectedItem = this.optionsCopy[selectedItemIndex].name
      this.selectedItemBadgeText = this.optionsCopy[selectedItemIndex].badgeText
    }
  }

  @Watch('setYear')
  onSetYearUpdate() {
    this.initYear()
  }

  componentWillLoad() {
    this.checkScreen()

    if (this.yearSelector) {
      this.initYear()
    }

    this.onOptionsUpdate()
  }

  @Method()
  async toggleDropdown() {
    this.lastChanged = new Date().getTime()
    this.isOpen = !this.isOpen
  }

  @Method()
  async getValue() {
    return this.value()
  }

  initYear() {
    let currentYear = null

    if (this.setYear) {
      currentYear = this.setYear
      this.selectedYear = +this.setYear
      this.selectedItem = this.setYear
    }
    else {
      currentYear = new Date().getFullYear()
    }

    this.updateOptionsYearSelector(currentYear)
  }

  updateOptionsYearSelector(currentYear: number) {
    const data: DropdownMenuItem[] = []
    this.currentYear = +currentYear

    for (let i = 0; i < 4; i++) {
      const year = {
        name: currentYear.toString(),
        text: currentYear.toString(),
        value: currentYear.toString(),
        selected: +currentYear === this.selectedYear,
      }
      data.push(year)

      currentYear++
    }
    this.options = data
  }

  itemClicked = (ev: CustomEvent<DropdownMenuItemClicked>) => {
    this.value = ev.detail.value

    if (this.yearSelector) {
      this.selectedYear = +this.value
    }

    if (!this.multi) {
      if (this.trackCurrentSelection) {
        this.filteredOptionsCopy = [
          ...this.filteredOptionsCopy.map(option => {
            return {
              ...option,
              selected: ev.detail.name === option.name,
            }
          }),
        ]

        this.optionsCopy = [
          ...this.optionsCopy.map(option => {
            return {
              ...option,
              selected: ev.detail.name === option.name,
            }
          }),
        ]
      }
      this.dropdownItemSelected.emit({ name: ev.detail.name, value: ev.detail.value })
      this.dropdownUpdated.emit(this.optionsCopy)
      this.isOpen = false

      this.selectedItem = ev.detail.name
      ev.detail.badgeText ? (this.selectedItemBadgeText = ev.detail.badgeText) : (this.selectedItemBadgeText = '')
    } else {
      this.filteredOptionsCopy = [
        ...this.filteredOptionsCopy.map(option => {
          return {
            ...option,
            selected: ev.detail.name === option.name ? !option.selected : option.selected,
          }
        }),
      ]

      const tempLocalSelectMenuOptions = [...this.optionsCopy]
      tempLocalSelectMenuOptions.forEach(opt => {
        const option = this.filteredOptionsCopy.find(el => el.name === opt.name)
        if (option) {
          opt.selected = option.selected
        }
      })
      this.optionsCopy = [...tempLocalSelectMenuOptions]
      this.dropdownUpdated.emit(this.optionsCopy)

      this.getSelectedItems()
    }
  }

  getSelectedItems() {
    this.itemSelected = 0
    const searchResult = this.optionsCopy.some(val => {
      if (val.selected) {
        this.selectedItem = val.name
        return true
      }
    })

    if (!searchResult) {
      this.selectedItem = null
    }

    this.optionsCopy.map(opt => {
      if (opt.selected) {
        this.itemSelected++
      }
    })
  }

  renderSearchField() {
    return (
      <div class="snap-dropdown-card-search">
        <input
          type="text"
          class={`snap-dropdown-search-field ${this.searchWord ? 'active' : ''}`}
          placeholder="Search"
          autoComplete="off"
          value={this.searchWord}
          ref={el => (this.textInput = el as HTMLInputElement)}
          onInput={event => {
            this.onInput(event)
          }}
        />
        {!this.searchWord && <snap-icon class="snap-dropdown-card-icon-search" icon="search-solid" color="#94A3B8" size="sm"></snap-icon>}
        {this.searchWord && (
          <snap-icon
            class="snap-dropdown-card-icon-close"
            icon="x-line"
            color="#64748B"
            size="sm"
            onClick={() => {
              this.clearSearchField()
            }}></snap-icon>
        )}
      </div>
    )
  }

  clearSearchField() {
    this.textInput.value = ''
    this.searchWord = ''
    this.filteredOptionsCopy = [...this.optionsCopy]
  }

  onInput(event) {
    this.searchWord = event.target.value.trim()
    this.searchMethod === 'start'
      ? (this.filteredOptionsCopy = [...this.optionsCopy.filter(item => item.text.toLowerCase().startsWith(event.target.value.trim()))])
      : (this.filteredOptionsCopy = [...this.optionsCopy.filter(item => item.text.toLowerCase().includes(event.target.value.trim()))])
  }

  yearDown() {
    this.currentYear--
    this.updateOptionsYearSelector(this.currentYear)
  }

  yearUp() {
    this.currentYear++
    this.updateOptionsYearSelector(this.currentYear)
  }

  getChevronSize() {
    if (this.isMobile && (this.size === 'xs' || this.size === 'base' || this.size === 'lg' || this.size === 'xl')) return 'sm'
    else if (!this.isMobile && this.size === 'xl') return 'md'
    else if (this.size === 'xs') return 'xs'
  }

  dropdown = () => {
    const optionsLength = this.optionsCopy.length

    return [
      !!this.title && !!this.description && (
        <snap-dropdown-menu-header
          title={this.title}
          avatar-img={this.avatarImg}
          switch-profile={this.switchProfileLink}
          is-mobile={this.isMobile}>
          {this.description}
        </snap-dropdown-menu-header>
      ),
      <div>{this.searchField && this.renderSearchField()}</div>,
      <div class="icon-year-up">
        {this.yearSelector && (
          <div class="icon-year-up-container" onClick={() => this.yearDown()}>
            <snap-icon icon="chevron-up-solid" color="#64748B" size="sm"></snap-icon>
          </div>
        )}
      </div>,
      <ul class={this.isMobile ? 'mobile' : ''}>
        {this.filteredOptionsCopy.map((option, index) => {
          return (
            <span>
              {index < optionsLength &&
                index !== 0 &&
                this.filteredOptionsCopy[index].section !== this.filteredOptionsCopy[index - 1].section &&
                !!option.section && <div class="list-menu-section">{option.section}</div>}
              {index === 0 && !!option.section && <div class="list-menu-section">{option.section}</div>}

              <snap-dropdown-menu-item
                key={option.value + '-' + index}
                value={option.value}
                name={option.name}
                active={option.selected}
                divider={option.divider}
                icon={option.icon}
                badgeText={option.badgeText}
                target={option.target}
                href={option.href}>
                {this.multi && <snap-checkbox-button checked={option.selected}></snap-checkbox-button>}
                {option.text}
              </snap-dropdown-menu-item>
            </span>
          )
        })}
      </ul>,
      <div class="icon-year-down">
        {this.yearSelector && (
          <div class="icon-year-up-container" onClick={() => this.yearUp()}>
            <snap-icon icon="chevron-down-solid" color="#64748B" size="sm"></snap-icon>
          </div>
        )}
      </div>,
    ]
  }

  getListMenuClasses() {
    const classes = ['list-menu']

    if (this.leftHang || this.yearSelector) classes.push('list-menu--left')
    if (!!this.title && !this.description) classes.push('with-header')
    this.searchField && classes.push('with-search')
    this.menuPosition === 'top' && classes.push('menu-top')

    return classes.join(' ').toString()
  }

  render() {
    return (
      <Host
        class={{
          'minimal': this.minimal,
          'no-button': this.hideButton,
          'year-picker': this.yearSelector,
        }}
        onDropdownMenuItemClicked={this.itemClicked}>
        {!this.hideButton && !this.minimal && (
          <button
            class={`
            snap-dropdown-button
            snap-dropdown-button-${this.size}
            ${this.isOpen ? 'snap-dropdown-button-open' : ''}
            ${this.disabled ? 'snap-dropdown-button-disabled' : ''}
            ${this.leadingLabel ? 'snap-dropdown-leading-label' : ''}
            ${this.selectedItem ? 'snap-dropdown-has-value' : ''}`}
            onClick={() => this.toggleDropdown()}>
            {this.leadingLabel && <div class="snap-dropdown-button-leading-label"> {this.leadingLabel}</div>}
            {this.icon && <snap-icon class="snap-dropdown-button-icon" icon={this.icon} size="xs"></snap-icon>}

            {this.selectedItem ? (
              <span class="selected-item-container">
                <span class="has-value">{this.selectedItem}</span>
                {this.selectedItemBadgeText && <snap-indicator class="snap-indicator-gray" size="tiny" text={this.selectedItemBadgeText} />}
              </span>
            ) : (
              <span>{this.buttonText}</span>
            )}
            {this.itemSelected > 1 && (
              <snap-indicator class="snap-indicator-gray" size="sm" text={`+${this.itemSelected - 1}`}></snap-indicator>
            )}
            {this.indicator && !this.multi && (
              <snap-indicator class="snap-dropdown-button-indicator" size="tiny" color="gray" text={this.indicator}></snap-indicator>
            )}

            <snap-icon size="sm" class="arrow" icon={this.isOpen ? 'chevron-up-solid' : 'chevron-down-solid'} />
          </button>
        )}
        {!!this.minimal && (
          <button
            class={`
            snap-dropdown-button-minimal
            ${this.isOpen ? 'snap-dropdown-button-minimal-open' : ''}
            ${this.disabled ? 'snap-dropdown-button-minimal-disabled' : ''}`}
            onClick={() => this.toggleDropdown()}>
            <snap-icon class="snap-icon-button-minimal" size="sm" icon="dots-vertical-line" state="rounded" bg-color="#fff" />
          </button>
        )}
        {!!this.isOpen && !this.isMobile && (
          <div style={{ maxHeight: `${this.maxHeight}` }} class={this.getListMenuClasses()}>
            {this.dropdown()}
          </div>
        )}
        {!!this.isOpen && !!this.isMobile && this.modalType === 'drawer' && (
          <snap-action-sheet
            onSnap-action-sheet-close={() => {
              this.isOpen = false
            }}
            header={this.buttonText}>
            {this.dropdown()}
          </snap-action-sheet>
        )}

        {!!this.isOpen && !!this.isMobile && this.modalType === 'fullscreen' && (
          <div class={`dropdown-list-modal ${this.isOpen ? 'open' : ''}`}>
            <div class="dropdown-list-modal-backdrop"></div>
            <div class="dropdown-list-modal-container">
              <div class="dropdown-list-modal-header">
                <div class="modal-header-title">{this.buttonText}</div>
                <snap-icon
                  class="modal-header-close"
                  icon="x-line"
                  color="#64748B"
                  size="md"
                  onClick={() => (this.isOpen = false)}></snap-icon>
              </div>
              <div class="dropdown-list-modal-body">{this.dropdown()}</div>
            </div>
          </div>
        )}
      </Host>
    )
  }
}
